// 🔧 store_repository.js
// ✅ Purpose: Firestore repository for Event Merch Store demo data
// ✅ Features:
// - Writes store product documents
// - Writes product variant subcollection documents
// - Uses Firestore-generated document IDs
// - Uses production collection path
// - No business logic
// - Fully block-commented for clarity and future devs

import {
  collection,
  doc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../../../firebase";

export const saveStoreProducts = async ({
  demoContext,
  products = [],
  variants = [],
} = {}) => {
  console.log("💾 Saving Store Engine demo data.");

  const eventId =
    demoContext?.eventId ||
    demoContext?.eventResult?.eventId ||
    demoContext?.event?.eventId;

  if (!eventId) {
    throw new Error("Store Repository requires an eventId.");
  }

  if (products.length === 0) {
    return {
      products: [],
      variants: [],
    };
  }

  const batch = writeBatch(db);

  const savedProducts = [];
  const savedVariants = [];

  products.forEach((product) => {
    const productRef = doc(
      collection(db, "events", eventId, "store_items")
    );

    const productVariants = variants.filter(
      (variant) => variant.productLocalKey === product.localKey
    );
    const receivedInventory = productVariants.reduce(
      (sum, variant) => sum + Number(variant.receivedInventory || 0),
      0
    );
    const availableInventory = productVariants.reduce(
      (sum, variant) => sum + Number(variant.availableInventory || 0),
      0
    );
    const soldInventory = productVariants.reduce(
      (sum, variant) => sum + Number(variant.soldInventory || 0),
      0
    );
    const reservedInventory = productVariants.reduce(
      (sum, variant) => sum + Number(variant.reservedInventory || 0),
      0
    );
    const variantPrices = productVariants
      .map((variant) => Number(variant.priceCents || 0))
      .filter((price) => price >= 0);

    const productData = {
      ...product,
      eventId,
      hasVariants: productVariants.length > 0,
      totalReceivedInventory: receivedInventory,
      totalAvailableInventory: availableInventory,
      totalSoldInventory: soldInventory,
      totalReservedInventory: reservedInventory,
      minPriceCents: variantPrices.length ? Math.min(...variantPrices) : 0,
      maxPriceCents: variantPrices.length ? Math.max(...variantPrices) : 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      demoSeed: true,
    };

    batch.set(productRef, productData);

    savedProducts.push({
      ...productData,
      id: productRef.id,
    });

    productVariants
      .forEach((variant) => {
        const variantRef = doc(
          collection(
            db,
            "events",
            eventId,
            "store_items",
            productRef.id,
            "variants"
          )
        );

        const variantData = {
          ...variant,
          productId: productRef.id,
          eventId,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          demoSeed: true,
        };

        batch.set(variantRef, variantData);

        savedVariants.push({
          ...variantData,
          id: variantRef.id,
        });
      });
  });

  batch.set(doc(db, "events", eventId, "settings", "store"), {
    storeEnabled: true,
    storeDescription: "Shop event merchandise and support this event.",
    storeBannerImageUrl: "/images/social-share-car-shows.png",
    pickupEnabled: true,
    pickupInstructions: "Pick up your order at the event merchandise booth.",
    shippingEnabled: true,
    flatShippingCents: 500,
    freeShippingThresholdCents: 1000,
    orderEmailMessage: "Thank you for supporting this event. We will have your order ready soon.",
    demoSeed: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });

  await batch.commit();

  console.log("✅ Store Engine repository completed.", {
    products: savedProducts.length,
    variants: savedVariants.length,
  });

  return {
    products: savedProducts,
    variants: savedVariants,
  };
};
